
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';
import styles from './post-body.module.css';
import { BLOG_CATEGORIES, buildBlogPostingJsonLd, decodeHtmlEntities, isServedBlogPost, rewriteContentImages, serializeJsonLd, toIsoUtc, toMetaDescription, } from '@/lib/blogSeo';
import { prepareBlogTables } from '@/lib/blogTables';
import formatDate from '@/lib/formatDate';
import Breadcrumbs from '@/components/atoms/Breadcrumbs';
import CallToAction from '@/components/CallToAction';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';
import { getAllBlogPosts, getPostAndMorePosts } from '@/utils/wordpress';
import { Avatar, PostPreview } from '.';
import NotFoundPage from '../404';
type PostSeo = {
    description: string;
    datePublished: string | null;
    dateModified: string | null;
    jsonLd: string;
};
const Post = ({ post, posts, seo, }: {
    post: any;
    posts: any;
    seo: PostSeo;
}) => {
    const router = useRouter();
    const morePosts = posts?.edges;
    if (!router.isFallback && (!post?.slug || !post?.title)) {
        return <NotFoundPage />;
    }
    if (!post?.title) {
        return <NotFoundPage />;
    }
    return (<main className='dark:bg-slate-900'>
      <Seo templateTitle={decodeHtmlEntities(post.title)} description={seo?.description} image={post.featuredImage?.node.sourceUrl} type='article' date={seo?.datePublished || undefined} structuredData={false}/>
      {seo && (<Head>
          {seo.dateModified && (<meta property='article:modified_time' content={seo.dateModified}/>)}
          <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: seo.jsonLd }}/>
        </Head>)}
      <div className='relative  '>
        <div className='mx-auto'>
          <div className='relative z-10  pb-8 sm:pb-16 md:pb-20 lg:w-full '>
            <LandingPageNavigation />
          </div>
        </div>
      </div>
      {router.isFallback ? (<>Loading…</>) : (<>
          <article className='mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8 '>
            <div className='mb-10'>
              <Breadcrumbs pages={[
                { name: 'Blog', href: '/blog', current: false },
                { name: post.title, href: '', current: true },
            ]}/>
            </div>
            <PostHeader title={post.title} coverImage={post.featuredImage} date={post.date} modified={post.modified} datePublished={seo?.datePublished} dateModified={seo?.dateModified} author={post.author} categories={post.categories}/>
            <PostBody content={post.content}/>
          </article>

          <SectionSeparator />

          <CallToAction />

          {morePosts.length > 0 && <MoreStories posts={morePosts}/>}
        </>)}
      <Footer />
    </main>);
};
function MoreStories({ posts }: any) {
    return (<section className='relative mx-auto mb-14 max-w-7xl'>
      <div className='text-center'>
        <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
          More Stories
        </h2>
      </div>
      <div className='mx-auto mt-12 grid max-w-lg gap-5 lg:max-w-none lg:grid-cols-3'>
        {posts.map(({ node }: any) => (<PostPreview key={node.slug} title={node.title} coverImage={node.featuredImage} date={node.date} dateGmt={node.dateGmt} author={node.author} slug={node.slug} excerpt={node.excerpt}/>))}
      </div>
    </section>);
}
function PostBody({ content }: any) {
    return (<div className='mx-auto max-w-3xl'>
      {/* Images are already routed through the image CDN in getStaticProps. */}
      <div className={styles.content} dangerouslySetInnerHTML={{ __html: content }}/>
    </div>);
}
function SectionSeparator() {
    return <hr className='mb-24  mt-28 border-gray-300 dark:border-white/25'/>;
}
function PostTitle({ children }: any) {
    return (<h1 className='mb-3 text-3xl font-bold leading-8 tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl' dangerouslySetInnerHTML={{ __html: children }}/>);
}
function PostHeader({ title, coverImage, date, modified, datePublished, dateModified, author, categories, }: any) {
    // Check if the post was updated (modified date is different from posted date)
    const wasUpdated = modified && date && formatDate(modified) !== formatDate(date);
    return (<div className='mx-auto max-w-prose text-lg'>
      <PostTitle>{title}</PostTitle>
      <div className='hidden md:mb-6 md:block'></div>
      <div className='mx-auto max-w-3xl'>
        {/* <div className='block mb-6 mt-6'>
          <Avatar author={author} />
        </div> */}
        <div className='mb-6 text-sm text-zinc-400 dark:text-zinc-500'>
          Posted{' '}
          <time dateTime={datePublished || date}>{formatDate(date)}</time>
          {wasUpdated && (<>
              {' '}
              · Updated{' '}
              <time dateTime={dateModified || modified}>
                {formatDate(modified)}
              </time>
            </>)}
          <Avatar author={author}/>
          {/* <Categories categories={categories} /> */}
        </div>
      </div>
    </div>);
}
const getStaticProps = async ({ params, preview = false, previewData, locale, }: any) => {
    //redirect 301 to the /blog
    if (locale !== 'en') {
        return {
            redirect: {
                destination: `/blog`,
                permanent: true,
            },
        };
    }
    // WordPress slugs are always lowercase and WPGraphQL matches them exactly,
    // so /blog/How-To-X found nothing and crashed with a 500.
    const slug = typeof params?.slug === 'string' ? params.slug : '';
    if (!preview && slug !== slug.toLowerCase()) {
        return {
            redirect: { destination: `/blog/${slug.toLowerCase()}`, permanent: true },
        };
    }
    const data = await getPostAndMorePosts(slug, preview, previewData, 'Uncategorized', BLOG_CATEGORIES);
    // A slug WordPress doesn't know returns `post: null`; this used to read
    // `data.post.title` and answer every unknown /blog URL with a 500.
    if (!data?.post?.title) {
        return {
            notFound: true,
        };
    }
    const post = {
        ...data.post,
        content: prepareBlogTables(rewriteContentImages(data.post.content || '')),
    };
    const title = decodeHtmlEntities(post.title);
    const description = toMetaDescription(post.excerpt || post.content || '');
    const datePublished = toIsoUtc(post.dateGmt) || null;
    const dateModified = toIsoUtc(post.modifiedGmt) || datePublished;
    const author = post.author?.node;
    const authorName = author?.firstName && author?.lastName
        ? `${author.firstName} ${author.lastName}`
        : author?.name || null;
    return {
        props: {
            preview,
            post,
            posts: data.posts,
            seo: {
                description,
                datePublished,
                dateModified,
                jsonLd: serializeJsonLd(buildBlogPostingJsonLd({
                    slug: post.slug,
                    title,
                    description,
                    image: post.featuredImage?.node?.sourceUrl,
                    datePublished: datePublished || undefined,
                    dateModified: dateModified || undefined,
                    authorName,
                })),
            },
        },
        revalidate: 10,
    };
};
export const getStaticPaths = async () => {
    const allPosts = await getAllBlogPosts();
    return {
        paths: allPosts
            .filter(isServedBlogPost)
            .map((post: any) => ({ params: { slug: `${post.slug}` } })),
        fallback: true,
    };
};
export default Post;

    async function __Next_Translate__getStaticProps__1a167d9f3d0__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/blog/[slug]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a167d9f3d0__ as getStaticProps }
  